import greetingGlyph from "@/assets/ai-chat-actions/greeting-glyph.svg";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { memo } from "react";

// Path derived by applying the Figma transforms (scaleY(-1) ∘ rotate(129°) +
// translate(75.82, 0)) to the original asset's bezier coordinates.
const CurvedArrow = () => (
  <svg
    width="161"
    height="163"
    viewBox="0 0 161 163"
    fill="none"
    aria-hidden
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M75.75 0.5 C91.9 45.5,89.6 82.8,65.9 91.5 C23.4 107,26.4 61,47.9 61 C70.5 61,76.1 81.3,79.9 100.5 C81.4 108,83.9 128.5,83.9 158.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeDasharray="1 2"
      opacity="0.5"
    />
    {/* Arrowhead — two short arms pointing down */}
    <path
      d="M79 153 L83.5 158.5 L88 153"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.5"
      fill="none"
    />
  </svg>
);

// Exact reproduction of Figma's "shadow blur" asset (node 473:1471):
// a filled circle + wide stroke, both Gaussian-blurred, creating a luminous orb.
const MicGlow = () => (
  <svg
    width="282"
    height="282"
    viewBox="0 0 282.2 282.2"
    fill="none"
    aria-hidden
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", overflow: "visible" }}
  >
    <defs>
      <filter
        id="mic-glow-blur"
        x="0"
        y="0"
        width="282.2"
        height="282.2"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="22.3" result="effect1_foregroundBlur" />
      </filter>
    </defs>
    <g filter="url(#mic-glow-blur)">
      <circle cx="141.1" cy="141.1" r="78.5" fill="#AB47DE" />
      <circle cx="141.1" cy="141.1" r="78.5" stroke="#3E1B84" strokeWidth="36" />
    </g>
  </svg>
);

const MicIcon = () => (
  <svg width="40" height="40" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.2218 22V11C14.2218 8.81196 15.0413 6.71354 16.5 5.16637C17.9587 3.61919 19.9371 2.75 22 2.75C24.0629 2.75 26.0413 3.61919 27.5 5.16637C28.9587 6.71354 29.7782 8.81196 29.7782 11V22C29.7782 24.188 28.9587 26.2865 27.5 27.8336C26.0413 29.3808 24.0629 30.25 22 30.25C19.9371 30.25 17.9587 29.3808 16.5 27.8336C15.0413 26.2865 14.2218 24.188 14.2218 22ZM35.75 22C35.75 21.6353 35.6051 21.2856 35.3473 21.0277C35.0894 20.7699 34.7397 20.625 34.375 20.625C34.0103 20.625 33.6606 20.7699 33.4027 21.0277C33.1449 21.2856 33 21.6353 33 22C33 24.9174 31.8411 27.7153 29.7782 29.7782C27.7153 31.8411 24.9174 33 22 33C19.0826 33 16.2847 31.8411 14.2218 29.7782C12.1589 27.7153 11 24.9174 11 22C11 21.6353 10.8551 21.2856 10.5973 21.0277C10.3394 20.7699 9.98967 20.625 9.625 20.625C9.26033 20.625 8.91059 20.7699 8.65273 21.0277C8.39487 21.2856 8.25 21.6353 8.25 22C8.25419 25.4075 9.52174 28.6924 11.8075 31.2195C14.0933 33.7466 17.235 35.3363 20.625 35.6813V41.25C20.625 41.6147 20.7699 41.9644 21.0277 42.2223C21.2856 42.4801 21.6353 42.625 22 42.625C22.3647 42.625 22.7144 42.4801 22.9723 42.2223C23.2301 41.9644 23.375 41.6147 23.375 41.25V35.6813C26.765 35.3363 29.9067 33.7466 32.1925 31.2195C34.4783 28.6924 35.7458 25.4075 35.75 22Z" fill="white"/>
    <rect x="15" y="41" width="14" height="3" rx="1.5" fill="white"/>
  </svg>
);

interface ChatMealPickerGreetingProps {
  onAddMeal?: (visibleText: string, agentPrompt: string) => void;
  disabled?: boolean;
  onMicPress?: () => void;
}

export const ChatMealPickerGreeting = memo(function ChatMealPickerGreeting({
  onMicPress,
}: ChatMealPickerGreetingProps) {
  const { user } = useGetUser();
  const firstName = user?.personalInfo?.firstName?.trim();

  return (
    <div className="content-class flex min-h-full w-full flex-col items-center overflow-x-hidden">
      {/* Glyph + heading + hint — Figma container 632:454 (gap-[24px] between the
          glyph and the text block, gap-[16px] between heading and hint). */}
      <div className="mt-[44px] flex flex-col items-center gap-6">
        <img src={greetingGlyph} alt="" aria-hidden className="h-[43px] w-[59.538px]" />

        <div className="flex flex-col items-center gap-4">
          <div
            className="w-[272px] text-center text-[35px] leading-[45px] font-semibold tracking-[-1px] text-white"
            style={{ fontFamily: "var(--font-crimson-text)" }}
          >
            <p className="m-0">
              Hey
              {firstName ? (
                <>
                  {" "}
                  <em className="text-[#bc9cf2]">{firstName}</em>.
                </>
              ) : (
                "."
              )}
            </p>
            <p className="m-0">How can I help?</p>
          </div>

          <p className="w-[294px] text-center text-sm leading-5 text-white/70">
            Track food. Get meal ideas. Ask for guidance.
          </p>
        </div>
      </div>

      {/* Arrow — 15.5px gap after text block (Figma: text ends y=397, arrow starts y=412.5) */}
      <div className="mt-4">
        <CurvedArrow />
      </div>

      {/* Mic button + glow — 23px gap after arrow (Figma: arrow ends y=574.8, mic starts y=598) */}
      <div className="relative mt-[23px] flex items-center justify-center">
        {/* Glow SVG: 282px, center 17px below button center (Figma: glow center=658, mic center=641) */}
        <div
          aria-hidden
          className="pointer-events-none absolute"
          style={{
            top: "50%",
            left: "50%",
            opacity: "0.5",
            transform: "translate(-50%, calc(-50% + 17px))",
          }}
        >
          <MicGlow />
        </div>

        <button
          type="button"
          aria-label="Use microphone"
          onClick={onMicPress}
          className="relative z-10 flex size-[84px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-[#b489f7] transition-[transform,box-shadow,filter] duration-100 active:scale-95 active:shadow-none active:brightness-90"
          style={{
            background: "linear-gradient(180deg, #7740f2 0%, #411f9a 100%)",
            boxShadow: "0px 10px 6px rgba(0,0,0,0.21)",
          }}
        >
          <MicIcon />
        </button>
      </div>
    </div>
  );
});
